import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, CheckCircle2, ExternalLink, GraduationCap, Highlighter, Loader2, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { api, ApiError, type BacklinkLesson, type BacklinkPlacement, type RemovedBacklink } from "../../lib/api.js";

/**
 * Every inbound internal link our backlink passes wrote, where it sits, and the
 * sentence that carries it — read live from Sanity, so a Studio edit shows here.
 *
 * Built after the first topic-matched night (2026-10-04) put 32 generated
 * sentences live and the only way to read them was a database snapshot. A
 * flagged sentence can be rewritten here: ask the model for a suggestion, edit
 * it freely, save. The link itself never moves or disappears; only the wording
 * around it changes.
 */

const KIND_LABEL: Record<BacklinkPlacement["kind"], string> = {
  "body-sentence": "generated sentence",
  "body-standalone": "standalone paragraph (old)",
  "body-mention": "existing mention, wrapped",
  faq: "FAQ answer",
};

/**
 * The live page, scrolled to the sentence and highlighted, via a text fragment
 * (`#:~:text=start,end`): Chrome, Edge, Safari and current Firefox scroll to the
 * first match and mark it, with nothing needed on the site. Long sentences use
 * their first and last four words so a link mark in the middle cannot break the
 * match. Commas and dashes are fragment syntax, so they are percent-encoded.
 *
 * Text the site keeps collapsed (an FAQ answer inside a closed accordion) may
 * not be found; the page then simply opens at the top.
 */
function highlightUrl(pageUrl: string, sentence: string): string {
  const words = sentence.trim().split(/\s+/).filter(Boolean);
  const enc = (t: string) => encodeURIComponent(t).replace(/-/g, "%2D");
  const frag = words.length <= 8 ? enc(words.join(" ")) : `${enc(words.slice(0, 4).join(" "))},${enc(words.slice(-4).join(" "))}`;
  return `${pageUrl.split("#")[0]}#:~:text=${frag}`;
}

const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" }) : "—");

type Filter = "review" | "flagged" | "unrelated" | "edited" | "approved" | "recent" | "removed" | "all";

export function BacklinksTab() {
  // Default view is the review queue: everything not yet marked fine, flagged
  // first. 449 links on day one is not a list anyone reads twice; approving the
  // clean ones (one by one, or all unflagged at once) is what shrinks it.
  const [filter, setFilter] = useState<Filter>("review");
  // Rows touched in this session stay in view whatever the filter says. Fixing
  // a flagged sentence removes its flag, and the first edit on 2026-10-04 made
  // the row vanish from "Flagged" the moment it was saved.
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const touch = (id: string) => setTouched((s) => new Set(s).add(id));
  const [q, setQ] = useState("");
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["backlink-placements"], queryFn: () => api.getBacklinkPlacements() });
  const refresh = useMutation({
    mutationFn: () => api.getBacklinkPlacements(true),
    onSuccess: (d) => qc.setQueryData(["backlink-placements"], d),
  });
  const approve = useMutation({
    mutationFn: (v: { ids: string[]; approved: boolean }) => api.approveBacklinks(v.ids, v.approved),
    onMutate: (v) => {
      // Optimistic: the click should feel instant on a long list.
      v.ids.forEach(touch);
      qc.setQueryData(["backlink-placements"], (d: typeof list.data) =>
        d ? { ...d, placements: d.placements.map((p) => (v.ids.includes(p.id) ? { ...p, approved: v.approved } : p)) } : d);
    },
    onError: () => void qc.invalidateQueries({ queryKey: ["backlink-placements"] }),
  });

  const all = list.data?.placements ?? [];
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const needsLook = (p: BacklinkPlacement) => !p.approved;
  const rows = useMemo(() => {
    const needle = q.trim();
    return all
      .filter((p) =>
        touched.has(p.id) ||
        filter === "all" ||
        (filter === "review" && needsLook(p)) ||
        (filter === "flagged" && needsLook(p) && p.flags.length > 0) ||
        (filter === "edited" && !!p.editedAt) ||
        (filter === "approved" && p.approved) ||
        (filter === "unrelated" && p.relevance?.verdict === "none") ||
        (filter === "recent" && [p.createdAt, p.editedAt].some((d) => !!d && Date.parse(d) >= weekAgo)))
      .filter((p) => !needle || [p.hostTitle, p.targetTitle ?? "", p.targetKeyword ?? "", p.sentence].some((t) => t.includes(needle)))
      .sort((a, b) =>
        Number(b.flags.length > 0) - Number(a.flags.length > 0) ||
        (b.createdAt ?? "").localeCompare(a.createdAt ?? "") || a.targetSlug.localeCompare(b.targetSlug));
  }, [all, filter, q, weekAgo, touched]);

  if (list.isPending) return <div className="flex items-center gap-2 text-sm text-muted"><Loader2 className="h-4 w-4 animate-spin" /> Reading every page from Sanity…</div>;
  if (list.error) return <div className="text-sm text-danger">Could not read the links: {(list.error as Error).message}</div>;

  const toReview = all.filter(needsLook).length;
  const flagged = all.filter((p) => needsLook(p) && p.flags.length).length;
  const cleanInView = rows.filter((p) => !p.approved && p.flags.length === 0);
  const removed = list.data?.removed ?? [];
  const count: Record<Filter, number> = {
    review: toReview, flagged, unrelated: all.filter((p) => p.relevance?.verdict === "none").length, removed: removed.length,
    edited: all.filter((p) => p.editedAt).length, approved: all.filter((p) => p.approved).length,
    recent: all.filter((p) => [p.createdAt, p.editedAt].some((d) => !!d && Date.parse(d) >= weekAgo)).length, all: all.length,
  };
  const label: Record<Filter, string> = { review: "To review", flagged: "Flagged", unrelated: "Unrelated", edited: "Edited", approved: "Approved", recent: "Last 7 days", removed: "Removed", all: "All" };
  return (
    <div className="space-y-3">
      <LearningPanel />
      <RelevancePanel onDone={() => void qc.invalidateQueries({ queryKey: ["backlink-placements"] })} />
      <div className="card flex flex-wrap items-center gap-3 p-3 text-sm">
        <span><b>{all.length}</b> links · <span className={toReview ? "text-warn" : "text-accent"}><b>{toReview}</b> to review</span> ({flagged} flagged)</span>
        <div className="flex flex-wrap gap-1">
          {(Object.keys(label) as Filter[]).map((f) => (
            <button key={f} className={`btn-sm ${filter === f ? "btn-primary" : "btn-secondary"}`} onClick={() => { setFilter(f); setTouched(new Set()); }}>
              {label[f]} ({count[f]})
            </button>
          ))}
        </div>
        <input className="input min-w-[200px] flex-1" dir="rtl" placeholder="חיפוש לפי מאמר, עמוד או טקסט" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn-secondary btn-sm flex items-center gap-1" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
          <RefreshCw className={`h-3.5 w-3.5 ${refresh.isPending ? "animate-spin" : ""}`} /> Re-read Sanity
        </button>
        <span className="text-2xs text-muted">read {fmtDate(list.data?.scannedAt)}</span>
      </div>

      {filter !== "removed" && cleanInView.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
          <span>{cleanInView.length} link(s) in this view have no flags.</span>
          <button className="btn-secondary btn-sm flex items-center gap-1" disabled={approve.isPending}
            onClick={() => {
              if (window.confirm(`Mark ${cleanInView.length} unflagged link(s) as fine? They leave the review queue; any that change later come back.`)) {
                approve.mutate({ ids: cleanInView.map((p) => p.id), approved: true });
              }
            }}>
            <Check className="h-3.5 w-3.5" /> Approve all {cleanInView.length} unflagged
          </button>
        </div>
      )}

      {filter === "removed" ? (
        removed.length === 0 ? <div className="text-sm text-muted">Nothing removed.</div> : removed.map((r) => <RemovedRow key={r.eventId} r={r} />)
      ) : rows.length === 0 ? (
        <div className="text-sm text-muted">{filter === "review" ? "Nothing left to review." : filter === "flagged" ? "No flagged sentences to review." : "Nothing matches."}</div>
      ) : (
        rows.map((p) => (
          <PlacementRow key={p.id} p={p} onSaved={() => touch(p.id)}
            onApprove={(approved) => approve.mutate({ ids: [p.id], approved })} />
        ))
      )}
    </div>
  );
}

/**
 * What the operator's edits have taught: link texts learned per article, and
 * general rules — proposed by "Learn from my edits", applied only once approved.
 */
function LearningPanel() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<{ id: number; rule: string } | null>(null);
  const learning = useQuery({ queryKey: ["backlink-learning"], queryFn: () => api.getBacklinkLearning() });
  const distill = useMutation({
    mutationFn: () => api.distillBacklinkLessons(),
    onSuccess: () => { setOpen(true); void qc.invalidateQueries({ queryKey: ["backlink-learning"] }); },
  });
  const decide = useMutation({
    mutationFn: (v: { id: number; status?: BacklinkLesson["status"]; rule?: string }) => api.decideBacklinkLesson(v.id, { ...(v.status ? { status: v.status } : {}), ...(v.rule ? { rule: v.rule } : {}) }),
    onSuccess: () => { setEditing(null); void qc.invalidateQueries({ queryKey: ["backlink-learning"] }); },
  });
  const d = learning.data;
  const lessons = d?.lessons ?? [];
  const proposed = lessons.filter((l) => l.status === "proposed");
  const approved = lessons.filter((l) => l.status === "approved");
  return (
    <div className="card space-y-2 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <GraduationCap className="h-4 w-4 text-brand" />
        <span className="font-medium">Learning from your edits</span>
        <span className="text-xs text-muted">
          {d?.anchorForms.length ?? 0} link text(s) learned · {approved.length} rule(s) in use
          {proposed.length ? <> · <span className="text-warn">{proposed.length} proposed, waiting for you</span></> : null}
        </span>
        <button className="btn-secondary btn-sm flex items-center gap-1" disabled={distill.isPending || !d?.undistilledEdits}
          title={d?.undistilledEdits ? "One model call reads your new edits and proposes general rules. Nothing applies until you approve it." : "No new edits since the last run"}
          onClick={() => distill.mutate()}>
          {distill.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          Learn from my edits ({d?.undistilledEdits ?? 0} new)
        </button>
        <button className="btn-ghost btn-sm" onClick={() => setOpen((o) => !o)}>{open ? "Hide" : "Show"}</button>
        {distill.data && <span className="text-2xs text-muted">read {distill.data.read} edit(s)</span>}
        {distill.error && <span className="text-2xs text-danger">{(distill.error as Error).message}</span>}
      </div>
      {(open || proposed.length > 0) && (
        <div className="space-y-2 text-xs">
          {proposed.length > 0 && (
            <div className="space-y-1">
              <div className="font-medium text-warn">Proposed rules — approve to apply them to every new link sentence and AI suggestion:</div>
              {proposed.map((l) => (
                <div key={l.id} className="flex flex-wrap items-start gap-2 rounded border border-warn/30 p-2">
                  {editing?.id === l.id
                    ? <textarea className="input min-h-[48px] flex-1 text-xs" value={editing.rule} onChange={(e) => setEditing({ id: l.id, rule: e.target.value })} />
                    : <div className="min-w-0 flex-1"><div>{l.rule}</div>{l.why && <div className="text-2xs text-muted">{l.why}</div>}</div>}
                  <button className="btn-primary btn-sm" onClick={() => decide.mutate({ id: l.id, status: "approved", ...(editing?.id === l.id ? { rule: editing.rule } : {}) })}>Approve</button>
                  {editing?.id !== l.id && <button className="btn-ghost btn-sm" onClick={() => setEditing({ id: l.id, rule: l.rule })}>Edit</button>}
                  <button className="btn-ghost btn-sm" onClick={() => decide.mutate({ id: l.id, status: "rejected" })}>Reject</button>
                </div>
              ))}
            </div>
          )}
          {open && (
            <>
              <div>
                <div className="font-medium">Rules in use</div>
                {approved.length === 0 ? <div className="text-muted">None yet.</div> : approved.map((l) => (
                  <div key={l.id} className="flex items-start gap-2"><span className="min-w-0 flex-1">• {l.rule}</span>
                    <button className="btn-ghost btn-sm" onClick={() => decide.mutate({ id: l.id, status: "rejected" })}>Stop using</button></div>
                ))}
              </div>
              <div>
                <div className="font-medium">Link texts learned (used for every link to that article)</div>
                {(d?.anchorForms.length ?? 0) === 0 ? <div className="text-muted">None yet — they are learned when you save a sentence whose link text is the keyword in natural Hebrew.</div>
                  : d!.anchorForms.map((f) => (
                    <div key={f.targetUrl} dir="rtl"><b>{f.phrase}</b> <span className="text-muted">← {f.targetUrl.replace(/^https?:\/\/[^/]+/, "")}</span></div>
                  ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Highlighted({ sentence, anchor }: { sentence: string; anchor: string }) {
  const i = anchor ? sentence.indexOf(anchor) : -1;
  if (i === -1) return <>{sentence}</>;
  return <>{sentence.slice(0, i)}<span className="rounded bg-brand/10 font-medium text-brand underline">{anchor}</span>{sentence.slice(i + anchor.length)}</>;
}

/** `value`, once it has stopped changing for `ms`. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = window.setTimeout(() => setV(value), ms); return () => window.clearTimeout(t); }, [value, ms]);
  return v;
}

/**
 * What will become the link, before saving — computed by the server with the
 * save's own logic, as the operator types. It used to state the rule ("the
 * link goes on <keyword>") rather than the result, so a link that stopped
 * short of a word the operator added ("…מהליקובקטר פילורי") was only visible
 * after saving.
 */
function LinkPreview({ id, sentence, linkText, onReplace }: { id: string; sentence: string; linkText: string; onReplace: (next: string) => void }) {
  const s = useDebounced(sentence, 350);
  const a = useDebounced(linkText, 350);
  const q = useQuery({
    queryKey: ["backlink-locate", id, s, a],
    queryFn: () => api.locateBacklinkText(id, s, a || undefined),
    enabled: s.trim().length > 2,
    staleTime: 60_000,
  });
  if (!q.data) return <div className="text-2xs text-muted">{q.isFetching ? "Checking the link text…" : ""}</div>;
  if (!q.data.linkText) {
    // Typed link text not in the sentence, but the automatic phrase is: offer
    // to put the typed text in its place. The field picks words; this is the
    // way to change them (2026-10-04: "ערכים גבוהים בהליקובקטר" typed, the
    // sentence still said "הליקובקטר ערכים גבוהים", and nothing helped).
    const r = q.data.replaceable;
    return (
      <div className="flex flex-wrap items-center gap-2 text-2xs">
        <span className="text-danger">{q.data.error}</span>
        {r && a && (
          <button className="btn-secondary btn-sm" dir="rtl" onClick={() => onReplace(q.data!.sentence.replace(r, a))}>
            Put “{a}” in place of “{r}”
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="rounded bg-canvas p-1.5 text-xs" dir="rtl">
      <span className="text-muted">The link will be: </span>
      <Highlighted sentence={q.data.sentence} anchor={q.data.linkText} />
      {!a && <span className="mr-2 text-2xs text-muted">(automatic — type the link text above to change it)</span>}
    </div>
  );
}

/**
 * The relevance check: one Haiku call per target article judges whether each
 * page linking to it genuinely leads there. Links judged unrelated land in the
 * "Unrelated" filter with a Remove button; nothing is removed automatically.
 */
function RelevancePanel({ onDone }: { onDone: () => void }) {
  const status = useQuery({
    queryKey: ["backlink-relevance"],
    queryFn: () => api.getBacklinkRelevanceStatus(),
    refetchInterval: (q) => (q.state.data?.job?.status === "running" ? 3000 : false),
  });
  const job = status.data?.job ?? null;
  const [wasRunning, setWasRunning] = useState(false);
  useEffect(() => {
    if (job?.status === "running") setWasRunning(true);
    else if (wasRunning && job) { setWasRunning(false); onDone(); }
  }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const start = useMutation({
    mutationFn: (force: boolean) => api.startBacklinkRelevance(force),
    onSuccess: () => void status.refetch(),
  });
  return (
    <div className="card flex flex-wrap items-center gap-3 p-3 text-sm">
      <Sparkles className="h-4 w-4 text-brand" />
      <span className="font-medium">Relevance check</span>
      {job?.status === "running" ? (
        <span className="flex items-center gap-1 text-xs text-muted">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> {job.done}/{job.targets} target articles · {job.unrelated} unrelated so far
          {job.current ? <span dir="rtl"> · {job.current}</span> : null}
        </span>
      ) : (
        <>
          <button className="btn-secondary btn-sm" disabled={start.isPending} onClick={() => start.mutate(false)}
            title="Asks the topic judge whether each page that links to an article genuinely leads there. Links checked before are skipped.">
            Check relevance of all links (AI)
          </button>
          {job && <button className="btn-ghost btn-sm" onClick={() => { if (window.confirm("Re-check every link, including ones already checked?")) start.mutate(true); }}>Re-check all</button>}
          {job?.status === "done" && <span className="text-xs text-muted">last run: {job.links} links in {job.targets} articles · <b className={job.unrelated ? "text-warn" : ""}>{job.unrelated} judged unrelated</b>{job.failed ? ` · ${job.failed} could not be checked` : ""} — see the "Unrelated" filter</span>}
          {job?.status === "error" && <span className="text-xs text-danger">{job.error}</span>}
          {start.error && <span className="text-xs text-danger">{(start.error as Error).message}</span>}
        </>
      )}
    </div>
  );
}

function RemovedRow({ r }: { r: RemovedBacklink }) {
  const qc = useQueryClient();
  const restore = useMutation({
    mutationFn: () => api.restoreBacklink(r.eventId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["backlink-placements"] }),
  });
  return (
    <div className="card space-y-1 p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted">removed {fmtDate(r.removedAt)} · link to</span>
        <span className="font-medium" dir="rtl">{r.targetUrl.replace(/^https?:\/\/[^/]+/, "")}</span>
        <span className="text-muted">from</span><span className="font-medium" dir="rtl">{r.hostTitle}</span>
        <button className="btn-secondary btn-sm ml-auto" disabled={restore.isPending} onClick={() => restore.mutate()}>{restore.isPending ? "Restoring…" : "Restore"}</button>
      </div>
      {r.sentence && <div className="rounded bg-canvas p-2 text-muted line-through" dir="rtl">{r.sentence}</div>}
      {restore.error && <div className="text-danger">{(restore.error as Error).message}</div>}
    </div>
  );
}

const REMOVE_WHAT: Record<BacklinkPlacement["editMode"], string> = {
  faq: "the whole FAQ entry (question and answer) that was generated for this link",
  append: "the sentence that was written for this link",
  standalone: "the paragraph that was written for this link",
  "in-place": "only the link (the author's words stay)",
};

function PlacementRow({ p, onSaved, onApprove }: { p: BacklinkPlacement; onSaved: () => void; onApprove: (approved: boolean) => void }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  // The exact text that becomes the link. Empty = automatic: the link phrase
  // (main keyword, or the title without its subtitle), in any word order. The
  // operator sets it when the right link text is something else — e.g. an old
  // whole-title anchor that should be just "סוגי רפואה אלטרנטיבית".
  const [linkText, setLinkText] = useState("");
  const [aiProblems, setAiProblems] = useState<string[]>([]);
  const [needsOverride, setNeedsOverride] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [learned, setLearned] = useState<string | null>(null);
  const [undone, setUndone] = useState(false);
  // Undo: the previous sentence goes back live, and the edit is marked so that
  // no learning path uses it — a mistaken save must not teach the system.
  const undo = useMutation({
    mutationFn: () => api.undoBacklinkEdit(p.id),
    onSuccess: () => {
      setUndone(true); setSaved(false); setLearned(null); setDraft(null);
      onSaved();
      void qc.invalidateQueries({ queryKey: ["backlink-placements"] });
      void qc.invalidateQueries({ queryKey: ["backlink-learning"] });
    },
  });
  const remove = useMutation({
    mutationFn: () => api.removeBacklink(p.id),
    onSuccess: () => { onSaved(); void qc.invalidateQueries({ queryKey: ["backlink-placements"] }); },
  });
  const askRemove = () => {
    if (window.confirm(`Remove this link? This removes ${REMOVE_WHAT[p.editMode]}. You can restore it from "Removed".`)) remove.mutate();
  };
  const askUndo = () => {
    if (window.confirm("Undo the last edit? The previous sentence goes back to the live page, and nothing will be learned from this edit.")) undo.mutate();
  };

  const suggest = useMutation({
    mutationFn: () => api.suggestBacklinkSentence(p.id),
    onSuccess: (r) => { setDraft(r.sentence); setAiProblems(r.problems); setNeedsOverride(null); setSaved(false); },
  });
  const save = useMutation({
    mutationFn: (override: boolean) => api.saveBacklinkSentence(p.id, draft ?? "", override, linkText.trim() || undefined),
    onSuccess: (r) => {
      setSaved(true); setDraft(null); setLinkText(""); setNeedsOverride(null); setAiProblems([]);
      setLearned(r.learnedPhrase ?? null);
      onSaved();
      void qc.invalidateQueries({ queryKey: ["backlink-placements"] });
      void qc.invalidateQueries({ queryKey: ["backlink-learning"] });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409 && e.data?.needsOverride) setNeedsOverride(e.message);
    },
  });

  // Approved and not being worked on: one line, out of the way.
  if (p.approved && draft === null && !saved && !undone) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-line px-3 py-1.5 text-xs text-muted">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-accent" />
        <span className="min-w-0 truncate" dir="rtl">{p.hostTitle} → <b>{p.anchor}</b></span>
        {p.editedAt && <span className="shrink-0 text-2xs">edited</span>}
        <span className="ml-auto flex shrink-0 gap-1">
          {p.undoable && <button className="btn-ghost btn-sm" disabled={undo.isPending} onClick={askUndo}>Undo edit</button>}
          <button className="btn-ghost btn-sm" onClick={() => onApprove(false)}>Unapprove</button>
        </span>
      </div>
    );
  }

  return (
    <div className={`card space-y-2 p-3 ${p.flags.length && !p.approved ? "border-warn/40" : ""}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="text-muted">link to</span>
        <a href={p.targetUrl} target="_blank" rel="noreferrer" className="font-medium text-brand hover:underline" dir="rtl">{p.targetTitle ?? p.targetSlug}</a>
        <span className="text-muted">placed in</span>
        {p.hostUrl
          ? <a href={p.hostUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 font-medium hover:underline" dir="rtl">{p.hostTitle}<ExternalLink className="h-3 w-3" /></a>
          : <span className="font-medium" dir="rtl">{p.hostTitle}</span>}
        <span className="rounded bg-line px-1.5 py-0.5 text-2xs text-muted">{KIND_LABEL[p.kind]}</span>
        {p.hostUrl && (
          <a href={highlightUrl(p.hostUrl, p.sentence)} target="_blank" rel="noreferrer" className="btn-secondary btn-sm flex items-center gap-1"
            title={p.kind === "faq" ? "Opens the page at this answer — if the FAQ is collapsed on the site, open it to see the highlight" : "Opens the live page scrolled to this sentence, highlighted"}>
            <Highlighter className="h-3.5 w-3.5" /> Open on page
          </a>
        )}
        <span className="ml-auto text-2xs text-muted">
          {p.createdAt ? `added ${fmtDate(p.createdAt)}${p.createdBy ? ` by ${p.createdBy}` : ""}` : "added before tracking began"}
          {p.editedAt ? ` · edited ${fmtDate(p.editedAt)}` : ""}
        </span>
      </div>

      {p.kind === "faq" && p.faqQuestion && <div className="text-xs text-muted" dir="rtl">ש: {p.faqQuestion}</div>}
      {p.editMode === "in-place" && p.editable && (
        <div className="text-2xs text-muted">This is the author's sentence with a link wrapped onto it. An edit replaces this one sentence; the rest of the paragraph stays as it is.</div>
      )}
      <div className="rounded bg-canvas p-2 text-sm leading-relaxed" dir="rtl">
        {p.editedAt && <div className="mb-1 text-2xs font-medium text-accent">Live now (edited):</div>}
        <Highlighted sentence={p.sentence} anchor={p.anchor} />
      </div>
      {p.originalSentence && p.originalSentence !== p.sentence && (
        <div className="rounded border border-dashed border-line p-2 text-xs text-muted" dir="rtl">
          <span className="font-medium">Originally:</span> <span className="line-through decoration-muted/50">{p.originalSentence}</span>
        </div>
      )}

      {p.flags.length > 0 && (
        <ul className="space-y-0.5">
          {p.flags.map((f) => <li key={f} className="flex items-start gap-1 text-2xs text-warn"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{f}</li>)}
        </ul>
      )}

      {p.editable ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {/* First choice when it exists: keep the sentence, swap only the
                link phrase. Rewriting whole sentences is what put the same
                sentence on several pages. */}
            {p.minimalFix && (
              <button className="btn-primary btn-sm flex items-center gap-1" title="Keeps the sentence; only the link phrase changes"
                onClick={() => { setDraft(p.minimalFix!); setLinkText(p.learnedPhrase ?? ""); setSaved(false); setAiProblems([]); }}>
                <Check className="h-3.5 w-3.5" /> Fix link text only
              </button>
            )}
            <button className="btn-secondary btn-sm flex items-center gap-1" disabled={suggest.isPending} onClick={() => suggest.mutate()}
              title="The AI changes as little as possible and avoids sentences already on other pages">
              {suggest.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Suggest a fix (AI)
            </button>
            {draft === null && <button className="btn-ghost btn-sm" onClick={() => { setDraft(p.sentence); setSaved(false); }}>Edit by hand</button>}
            {draft === null && !p.approved && (
              <button className="btn-secondary btn-sm ml-auto flex items-center gap-1" title="Nothing to change — remove it from the review queue" onClick={() => onApprove(true)}>
                <Check className="h-3.5 w-3.5 text-accent" /> Looks fine
              </button>
            )}
            {draft === null && (<>
              <button className={`${p.relevance?.verdict === "none" ? "btn-danger" : "btn-ghost !text-danger"} btn-sm flex items-center gap-1`} disabled={remove.isPending} onClick={askRemove}
              title={`Removes ${REMOVE_WHAT[p.editMode]} from the live page. Restorable from "Removed".`}>
              <Trash2 className="h-3.5 w-3.5" /> {remove.isPending ? "Removing…" : "Remove link"}
            </button>
            {remove.error && <span className="text-2xs text-danger">{(remove.error as Error).message}</span>}
            </>)}
            {saved && <span className="flex items-center gap-1 text-2xs text-accent"><CheckCircle2 className="h-3.5 w-3.5" /> Saved to the live page</span>}
            {draft === null && p.undoable && (
              <button className="btn-ghost btn-sm" disabled={undo.isPending} onClick={askUndo}
                title="Put the previous sentence back on the live page; nothing is learned from the undone edit">
                {undo.isPending ? "Undoing…" : "Undo edit"}
              </button>
            )}
            {undone && <span className="text-2xs text-accent">Edit undone — previous sentence restored; nothing learned from it.</span>}
            {undo.error && <span className="text-2xs text-danger">{(undo.error as Error).message}</span>}
            {learned && <span className="text-2xs text-brand" dir="rtl">Learned: links to this article will now say "{learned}"</span>}
            {suggest.error && <span className="text-2xs text-danger">{(suggest.error as Error).message}</span>}
          </div>
          {draft !== null && (
            <div className="space-y-1">
              <textarea className="input min-h-[64px] w-full text-sm" dir="rtl" value={draft} onChange={(e) => { setDraft(e.target.value); setNeedsOverride(null); }} />
              <div className="flex flex-wrap items-center gap-2 text-2xs">
                <label className="text-muted" htmlFor={`lt-${p.id}`}>Link text:</label>
                <input id={`lt-${p.id}`} className="input w-64 py-1 text-xs" dir="rtl" value={linkText}
                  placeholder={p.linkPhrase ? `auto: ${p.linkPhrase}` : "the words in the sentence that become the link"}
                  onChange={(e) => { setLinkText(e.target.value); setNeedsOverride(null); }} />
                {p.linkPhrase && linkText.trim() !== p.linkPhrase && (
                  <button className="btn-ghost btn-sm" onClick={() => setLinkText(p.linkPhrase)}>Use "{p.linkPhrase}"</button>
                )}
              </div>
              <LinkPreview id={p.id} sentence={draft} linkText={linkText.trim()} onReplace={(next) => { setDraft(next); setNeedsOverride(null); }} />
              {aiProblems.length > 0 && <div className="text-2xs text-warn">The suggestion still has: {aiProblems.join("; ")}</div>}
              <div className="flex flex-wrap items-center gap-2">
                <button className="btn-primary btn-sm" disabled={save.isPending || !draft.trim()} onClick={() => save.mutate(false)}>
                  {save.isPending ? "Saving…" : "Save to the live page"}
                </button>
                <button className="btn-ghost btn-sm" onClick={() => { setDraft(null); setLinkText(""); setNeedsOverride(null); setAiProblems([]); }}>Cancel</button>
                {needsOverride && (
                  <>
                    <span className="text-2xs text-warn">{needsOverride}</span>
                    <button className="btn-secondary btn-sm" onClick={() => save.mutate(true)}>Save anyway</button>
                  </>
                )}
                {save.error && !needsOverride && <span className="text-2xs text-danger">{(save.error as Error).message}</span>}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-2xs text-muted">
          <span className="text-warn">Can't edit here: {p.blockedReason ?? "edit it in Studio"}.</span>
          <button className={`${p.relevance?.verdict === "none" ? "btn-danger" : "btn-ghost !text-danger"} btn-sm flex items-center gap-1`} disabled={remove.isPending} onClick={askRemove}
              title={`Removes ${REMOVE_WHAT[p.editMode]} from the live page. Restorable from "Removed".`}>
              <Trash2 className="h-3.5 w-3.5" /> {remove.isPending ? "Removing…" : "Remove link"}
            </button>
            {remove.error && <span className="text-2xs text-danger">{(remove.error as Error).message}</span>}
          {!p.approved && (
            <button className="btn-secondary btn-sm ml-auto flex items-center gap-1" onClick={() => onApprove(true)}>
              <Check className="h-3.5 w-3.5 text-accent" /> Looks fine
            </button>
          )}
        </div>
      )}
    </div>
  );
}
